import React, { useState, useEffect } from 'react';
import { FiList, FiPlus, FiTrash2, FiExternalLink, FiSettings, FiX, FiBarChart } from 'react-icons/fi';
import { useAuth } from './contexts/AuthContext';
import LoginForm from './components/LoginForm';
import MetricsDashboard from './components/MetricsDashboard';
import api from './utils/api';
import { getVisitorId, getFingerprintData } from './utils/fingerprint';
import { Configurations } from './components/Configurations';
import { getFcmToken, initMessageHandlers } from './utils/firebase';


function App() {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('parse');
  const [url, setUrl] = useState('');
  const [parsedProduct, setParsedProduct] = useState(null);
  const [monitoredProducts, setMonitoredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [visitorId, setVisitorId] = useState(null);

  const loadMonitoredProducts = async () => {
    try {
      const response = await api.get(`/api/products`);
      if (response.data.success) {
        setMonitoredProducts(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleParse = async () => {
    if (!url.trim()) {
      setError('Please enter a product URL');
      return;
    }

    setLoading(true);
    setError('');
    setParsedProduct(null);

    try {
      const response = await api.post('/api/parse', { url: url.trim() });
      if (response.data.success) {
        setParsedProduct(response.data.data);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to parse product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMonitoring = async () => {
    if (!parsedProduct) return;

    try {
      const response = await api.post('/api/products', {
        url: url,
      });

      if (response.data.success) {
        setParsedProduct(null);
        setUrl('');
        setError('');
        await loadMonitoredProducts();
        setActiveTab('monitor');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add product to monitoring');
    }
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await api.delete(`/api/products/${productId}`);
      await loadMonitoredProducts();
    } catch (error) {
      console.error('Failed to remove product:', error);
    }
  };

  const formatPrice = (price) => {
    return price ? `${price.toFixed(2)} UAH` : 'N/A';
  };

  // Initialize FingerprintJS and get visitor ID
  useEffect(() => {
    const initializeFingerprint = async () => {
      try {
        const id = await getVisitorId();
        setVisitorId(id);
        console.log('Visitor ID:', id);
        
        // Send visitor data to server for analytics
        const fingerprintData = await getFingerprintData();
        await api.post('/api/analytics/visitor', {
          visitorId: id,
          fingerprint: fingerprintData,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error initializing fingerprint:', error);
      }
    };

    const initFcmToken = async () => {
      const token = await getFcmToken();
      console.log('initFcmToken', token);
    };

    if (isAuthenticated) {
      initializeFingerprint();
      initMessageHandlers();
      initFcmToken();
      loadMonitoredProducts();
    }
  }, [isAuthenticated]);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettings && !event.target.closest('.settings-dropdown')) {
        setShowSettings(false);
      }
      if (showIOSInstructions && !event.target.closest('.ios-instructions')) {
        setShowIOSInstructions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings, showIOSInstructions]);

  // Check iOS and PWA status
  useEffect(() => {
    const checkIOSAndPWA = () => {
      // Check if device is iOS
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      setIsIOS(iOS);
      
      // Check if app is running in standalone mode (installed as PWA)
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
      setIsStandalone(standalone);
    };

    checkIOSAndPWA();
  }, []);

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm />;
  }

  if (!visitorId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              {/* <FiShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" /> */}
              <img src="/logo.png" alt="Sales Checker" className="h-6 w-6 sm:h-8 sm:w-8" />
              <h1 className="ml-1 sm:ml-1 text-lg sm:text-2xl font-bold text-gray-900">
                Sales Checker
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Refresh button - only show in PWA mode */}
              {isStandalone && (
                <button
                  onClick={() => window.location.reload()}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh Page"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
              
              <div className="relative settings-dropdown">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Settings"
                >
                  <FiSettings className="h-5 w-5" />
                </button>
                
                {/* Settings Dropdown */}
                <div className={`absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-300 ease-in-out transform origin-top ${
                  showSettings 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}>
                  <Configurations
                    isShown={showSettings}
                    isIOS={isIOS}
                    isStandalone={isStandalone}
                    visitorId={visitorId}
                    onShowIOSInstructions={() => setShowIOSInstructions(true)}
                    onHideIOSInstructions={() => setShowIOSInstructions(false)}
                    onClose={() => setShowSettings(false)}
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('parse')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'parse'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiPlus className="inline mr-2" />
              Add Product
            </button>
            <button
              onClick={() => setActiveTab('monitor')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monitor'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiList className="inline mr-2" />
              Monitored Products ({monitoredProducts.length})
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'metrics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiBarChart className="inline mr-2" />
              Metrics Dashboard
            </button>
          </nav>
        </div>
      </div>

      {/* iOS Installation Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 ios-instructions">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Add to Home Screen</h2>
                <button
                  onClick={() => setShowIOSInstructions(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p className="mb-3">Follow these steps to add Sales Checker to your iPhone/iPad home screen:</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                        1
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Tap the Share button</p>
                        <p className="text-xs text-gray-500">Look for the square with an arrow pointing up in your browser's toolbar</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                        2
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Scroll down and tap "Add to Home Screen"</p>
                        <p className="text-xs text-gray-500">You'll find this option in the share menu</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                        3
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Customize the name (optional)</p>
                        <p className="text-xs text-gray-500">You can change the app name or keep "Sales Checker"</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                        4
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Tap "Add"</p>
                        <p className="text-xs text-gray-500">The app will be added to your home screen</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0 text-blue-600">💡</div>
                    <div className="text-xs text-blue-800">
                      <p className="font-medium mb-1">Benefits of installing:</p>
                      <ul className="space-y-1 text-blue-700">
                        <li>• Faster access to the app</li>
                        <li>• Works like a native app</li>
                        <li>• Receives push notifications</li>
                        <li>• No browser address bar</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={() => setShowIOSInstructions(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Got it
                  </button>
                  <button
                    onClick={() => {
                      setShowIOSInstructions(false);
                      setShowSettings(false);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-sm font-medium rounded-md text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'parse' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Add Product to Monitor
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                    Product URL (shop.metro.ua, eva.ua, or sisters.co.ua)
                  </label>
                  <input
                    type="url"
                    id="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://shop.metro.ua/product/... or https://eva.ua/ua/pr... or https://sisters.co.ua/..."
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  />
                </div>

                <button
                  onClick={handleParse}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Parsing...' : 'Parse Product'}
                </button>

                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}

                {parsedProduct && (
                  <div className="mt-6 border rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Parsed Product Data
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        {parsedProduct.image && (
                          <img
                            src={parsedProduct.image}
                            alt={parsedProduct.title}
                            className="w-full h-48 object-cover rounded-lg"
                          />
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Title</label>
                          <p className="text-sm text-gray-900">{parsedProduct.title}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Original Price</label>
                          <p className="text-lg font-semibold text-gray-900">
                            {formatPrice(parsedProduct.originalPrice)}
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Sale Price</label>
                          <p className={`text-lg font-semibold ${
                            parsedProduct.salePrice ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {formatPrice(parsedProduct.salePrice)}
                          </p>
                        </div>
                        
                        <button
                          onClick={handleAddToMonitoring}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          Add to Monitoring
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'monitor' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Monitored Products
                </h2>
              </div>
              
              {monitoredProducts.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No products being monitored. Add a product to get started!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Original Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sale Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Checked
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monitoredProducts.map((product) => (
                        <tr key={product._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-12 w-12">
                                <img
                                  className="h-12 w-12 rounded-lg object-cover"
                                  src={product.image}
                                  alt={product.title}
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {product.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <a
                                    href={product.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-blue-600 hover:text-blue-500"
                                  >
                                    View Product
                                    <FiExternalLink className="ml-1 h-3 w-3" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatPrice(product.originalPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-semibold ${
                              product.salePrice ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              {formatPrice(product.salePrice)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(product.lastChecked).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleRemoveProduct(product._id)}
                              className="text-red-600 hover:text-red-900 inline-flex items-center"
                            >
                              <FiTrash2 className="h-4 w-4 mr-1" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <MetricsDashboard 
            visitorId={visitorId} 
            monitoredProducts={monitoredProducts}
          />
        )}
      </main>
    </div>
  );
}

export default App;
